import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { useAgents } from '@/lib/queries/agents'
import { queryKeys } from '@/lib/queries/keys'
import { toast } from '@/components/ui/sonner'
import { wsClient } from '@/lib/ws/client'
import { recommendationKindLabel } from '@/lib/display'

// -----------------------------------------------------------------------------
// Schema do evento WS (VEC-408)
//
// Backend broadcasta no nível raiz da mensagem (sem wrapper `payload`).
// Validar o shape evita explodir a UI se backend mudar e drift acontecer.
// -----------------------------------------------------------------------------

const athenaRecommendationCreatedEventSchema = z.object({
  type: z.literal('athena_recommendation_created'),
  recommendation_id: z.string().min(1),
  target_agent_id: z.string().min(1).nullable().optional(),
  kind: z.enum([
    'hire_new_agent',
    'add_specialty',
    'rewrite_system_prompt',
    'create_specialty',
    'consolidate_agents',
  ]),
  confidence: z.number().min(0).max(1),
})

/**
 * Escuta o evento `athena_recommendation_created` no WS da company:
 *
 * - Invalida `queryKeys.athenaRecommendations.all` (re-fetch da lista nos
 *   contextos onde já está montada).
 * - Mostra toast com nome do target agent + botão "Ver" que navega para
 *   `/agents/recommendations`.
 *
 * Montado globalmente no `MainLayout` para que o usuário receba o aviso
 * em qualquer rota (não só na de recommendations).
 */
export function useAthenaRecommendationsWs(companyId: string | null): void {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Mapa de agentId → name resolvido localmente. Lista de agents da company
  // já vive no cache (carregada pelo Dashboard / Agents / etc.).
  const agentsQ = useAgents(companyId)

  useEffect(() => {
    if (!companyId) return

    const unsubscribe = wsClient.subscribe(companyId, (msg) => {
      const parsed = athenaRecommendationCreatedEventSchema.safeParse(msg)
      if (!parsed.success) return
      const event = parsed.data

      // Invalida todas as queries de recommendations (lista + detalhe).
      // Filtros diferentes têm chaves distintas, então invalidar `all`
      // garante refresh em todas as visões.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.athenaRecommendations.all,
      })

      const agentName = event.target_agent_id
        ? agentsQ.data?.find((a) => a.id === event.target_agent_id)?.name
        : null
      const subject = agentName
        ? `para ${agentName}`
        : event.target_agent_id
          ? 'para um agente'
          : 'de contratação de novo agente'

      toast.info('Athena criou uma nova recomendação', {
        description: `${recommendationKindLabel[event.kind]} ${subject} · ${Math.round(event.confidence * 100)}% confiança`,
        action: {
          label: 'Ver',
          onClick: () => navigate('/agents/recommendations'),
        },
      })
    })

    return unsubscribe
  }, [companyId, queryClient, navigate, agentsQ.data])
}
